<template>
    <div class="guided-regulations-review">
        <BCard class="guided-regulations-review-section mb-1">
        <template #header>
            <div class="guided-regulations-review-section__heading">
                <div>
                    <h5 class="mb-25">
                        {{ translate('Component(ResearchReviewProcess):legislations', 'Regulations') }}
                    </h5>
                    <p v-if="showDescription" class="text-less-muted mb-0">
                        {{ translate(
                            'Component(ResearchReviewProcess):legislations-description',
                            'Confirm regulation coverage, claims, evidence and exemptions extracted from the document.',
                        ) }}
                    </p>
                </div>
                <div class="guided-regulations-review-section__header-actions">
                    <BSpinner
                        v-if="resolvingRelatedSubstances"
                        small
                        variant="primary"
                        class="guided-regulations-review-section__resolver"
                    />
                    <BDropdown
                        right
                        variant="outline-primary"
                        class="guided-regulations-review-section__add"
                        toggle-class="text-nowrap"
                        :disabled="allRegulationsSelected"
                    >
                        <template #button-content>
                            <FeatherIcon icon="PlusIcon" size="13" class="mr-25" />
                            {{ translate('Component(ResearchReviewProcess):add-legislation', 'Add regulation') }}
                        </template>
                        <BDropdownItem
                            v-for="option in legislationOptions"
                            :key="option.id"
                            :disabled="option.disabled"
                            @click="addRegulation(option.id)"
                        >
                            {{ option.label }}
                        </BDropdownItem>
                    </BDropdown>
                    <BButton
                        v-if="allowRemoveRegulation && activeRegulation"
                        type="button"
                        variant="outline-danger"
                        class="btn-icon guided-regulations-review-section__remove"
                        :title="removeRegulationLabel"
                        @click="requestRemoveActiveRegulation"
                    >
                        <FeatherIcon icon="Trash2Icon" size="16" />
                    </BButton>
                </div>
            </div>
        </template>

        <BAlert v-if="relatedSubstancesError" variant="light-warning" show>
            <div class="alert-body">
                {{ translate(
                    'Component(ResearchReviewProcess):related-substances-load-failed',
                    'Related substance information could not be loaded. The review data is still available below.',
                ) }}
            </div>
        </BAlert>

        <div class="guided-regulations-review-section__tabs">
            <div class="guided-regulations-review-section__tabs-scroll" role="tablist">
                <div
                    v-for="entry in visibleRegulationEntries"
                    :key="regulationTabKey(entry.regulation, entry.index)"
                    class="guided-regulations-review-section__tab-item"
                >
                    <BButton
                        type="button"
                        variant="link"
                        role="tab"
                        class="guided-regulations-review-section__tab"
                        :class="{ 'guided-regulations-review-section__tab--active': activeRegulationIndex === entry.index }"
                        :aria-selected="activeRegulationIndex === entry.index ? 'true' : 'false'"
                        @click="selectRegulation(entry.index)"
                    >
                        {{ regulationTabLabel(entry.regulation, entry.index) }}
                        <span class="guided-regulations-review-section__tab-meta">
                            <BBadge
                                v-if="showTabSubstanceCount && tabSubstanceCount(entry.regulation)"
                                variant="light-secondary"
                                class="guided-regulations-review-section__tab-count"
                            >
                                {{ tabSubstanceCount(entry.regulation) }}
                            </BBadge>
                            <ReviewNeedsAttentionBadge
                                v-if="showTabAttention"
                                compact
                                :checks="tabAttentionChecks(entry.regulation, entry.index)"
                                class="guided-regulations-review-section__tab-attention"
                            />
                        </span>
                    </BButton>
                </div>
            </div>

        </div>

        <BAlert v-if="!visibleRegulationEntries.length" variant="light-secondary" show class="mb-0 mt-1">
            <div class="alert-body">
                {{ translate(
                    'Component(ResearchReviewProcess):no-legislations',
                    'No regulations were extracted from this document.',
                ) }}
            </div>
        </BAlert>

        <div
            v-else
            class="guided-regulations-review-section__active-content"
            role="tabpanel"
            :aria-label="regulationTabLabel(activeRegulation, activeRegulationIndex)"
        >
            <component
                :is="activeContentComponent"
                ref="activeContent"
                v-bind="activeContentProps"
                @select-substance-claim="selectSubstanceClaim"
                @edit-substance-declaration="editSubstanceDeclaration"
                @add-substance="addSubstance"
                @update-substance="updateSubstance"
                @select-substance-link="selectSubstanceLink"
                @remove-substance="removeSubstance"
                @update-exemptions="updateActiveExemptions"
                @update-scip-information="$emit('update-scip-information', $event)"
            />
        </div>

        </BCard>

        <GuidedUnassignedSubstancesSection
            ref="unassignedSubstances"
            :items="unmatchedSubstanceItems"
            :unassigned-link-count="substanceViewModel.unassignedLinks.length"
            :missing-regulation-substance-ids="missingRegulationSubstanceIds"
            :hydrating-ids="hydratingIds"
            :violations="violations"
            @update-substance="updateSubstance"
            @select-substance-link="selectUnassignedSubstanceLink"
            @remove-substance="removeSubstance"
        />
    </div>
</template>

<script>
import {
    BAlert,
    BBadge,
    BButton,
    BCard,
    BDropdown,
    BDropdownItem,
    BSpinner,
} from 'bootstrap-vue'
import { v4 as uuidv4 } from 'uuid'
import ReviewNeedsAttentionBadge from '../components/ReviewNeedsAttentionBadge.vue'
import GuidedUnassignedSubstancesSection from './GuidedUnassignedSubstancesSection.vue'
import substanceReviewCheckerRegistry from '../substances/substance-review-checker-registry'
import {
    buildSubstanceReviewViewModel,
    hasIdentity,
} from '../substances/substance-review-view-model'
import { getRelatedSubstances } from './substance-regulation-projection'
import { belongsToRegulation } from './regulation-review-view-model'
import {
    GuidedRegulationContentVariant,
    resolveGuidedRegulationContent,
} from './guided-regulation-content-registry'
import { resolveLegislationCapabilities } from './legislation-capability-registry'
import { substanceDeclarationPolicyEvaluator } from './substance-declaration-policy-evaluator'
import { legislationReviewPolicyProvider } from './legislation-review-policy-provider'
import { regulationVisibilityPolicyRegistry } from './regulation-visibility-policy-registry'
import {
    ADDABLE_SUBSTANCE_CLAIMS,
    MANAGED_SUBSTANCE_CLAIMS,
    SUBSTANCE_CLAIM_OPTIONS,
} from './substance-declaration-claims'
import { substanceViolationTarget } from '../../validation/symfony-property-path'

const clone = value => JSON.parse(JSON.stringify(value || []))
const createAttentionCheck = (
    id,
    passed,
    messageKey,
    fallbackMessage,
    { blocking = false, params = {} } = {},
) => ({
    id,
    status: passed ? 'passed' : 'needs_review',
    blocking,
    reason: { messageKey, fallbackMessage, params },
})
const normalizeExemptionName = value => (
    typeof value === 'string' ? value.trim() : ''
)
const uniqueExemptionNames = values => {
    const seen = new Set()

    return (Array.isArray(values) ? values : []).reduce((result, value) => {
        const name = normalizeExemptionName(value)
        const identity = name.toLowerCase()
        if (name && !seen.has(identity)) {
            seen.add(identity)
            result.push(name)
        }

        return result
    }, [])
}
const normalizeExemptionOption = value => {
    if (typeof value === 'string') {
        return normalizeExemptionName(value)
    }
    if (!value || typeof value !== 'object') {
        return ''
    }

    return normalizeExemptionName(
        value.key || value.code || value.title || value.name || String(value.id || ''),
    )
}

const normalizeSummary = summary => ({
    id: summary.id,
    name: summary.name || summary.title || String(summary.id),
    title: summary.name || summary.title || String(summary.id),
    cas: summary.cas || '',
    ec: summary.ec || '',
    echaId: summary.echaId || '',
    raw: summary.raw || summary,
})

export default {
    name: 'GuidedRegulationsReviewSection',
    components: {
        BAlert,
        BBadge,
        BButton,
        BCard,
        BDropdown,
        BDropdownItem,
        BSpinner,
        GuidedUnassignedSubstancesSection,
        ReviewNeedsAttentionBadge,
    },
    props: {
        legislations: {
            type: Array,
            default: () => [],
        },
        availableLegislations: {
            type: Array,
            default: () => [],
        },
        exemptions: {
            type: Array,
            default: () => [],
        },
        checkerRegistry: {
            type: Object,
            default: () => ({}),
        },
        relatedSubstancesProjection: {
            type: Object,
            default: () => ({ byId: {}, byKey: {} }),
        },
        resolvingRelatedSubstances: {
            type: Boolean,
            default: false,
        },
        relatedSubstancesError: {
            type: Boolean,
            default: false,
        },
        claimEditor: {
            type: String,
            default: 'structured',
        },
        showDescription: {
            type: Boolean,
            default: true,
        },
        showTabSubstanceCount: {
            type: Boolean,
            default: false,
        },
        showTabAttention: {
            type: Boolean,
            default: false,
        },
        hideEmptyNegativeSubstances: {
            type: Boolean,
            default: false,
        },
        hideSubstancesUntilDeclaration: {
            type: Boolean,
            default: false,
        },
        allowRemoveRegulation: {
            type: Boolean,
            default: false,
        },
        substanceDeclarationInference: {
            type: Object,
            default: () => ({}),
        },
        content: {
            type: Object,
            default: () => ({}),
        },
        visibilityPolicies: {
            type: Object,
            default: () => ({}),
        },
        scipInformation: {
            type: Object,
            default: null,
        },
        ensureScipRegulationForNumber: {
            type: Boolean,
            default: false,
        },
        violations: {
            type: Array,
            default: () => [],
        },
        substances: {
            type: Array,
            default: () => [],
        },
        substanceLinks: {
            type: Array,
            default: () => [],
        },
        missingRegulationSubstanceIds: {
            type: Array,
            default: () => [],
        },
        workspaceService: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            activeRegulationIndex: this.legislations.length ? 0 : null,
            summariesById: {},
            hydratingIds: [],
            hydrationRevision: 0,
            editingSubstanceDeclarations: {},
            pendingRegulationIdsByExtractedId: {},
            manualSubstanceSettingsByExtractedId: {},
            initialSubstanceDeclarationInferencePending: true,
        }
    },
    computed: {
        selectedLegislationIds() {
            return this.legislations
                .map(regulation => this.availableLegislationFor(regulation))
                .filter(Boolean)
                .map(regulation => regulation.id)
        },
        legislationOptions() {
            return this.availableLegislations
                .filter(legislation => {
                    const id = Number(legislation && legislation.id)

                    return Number.isInteger(id)
                        && id > 0
                        && typeof legislation.key === 'string'
                        && legislation.key.trim()
                })
                .map(legislation => ({
                    id: Number(legislation.id),
                    key: legislation.key.trim(),
                    label: legislation.title || legislation.name || legislation.key.trim(),
                    capabilities: legislation.capabilities,
                    reviewPolicy: legislation.reviewPolicy,
                    exemptions: uniqueExemptionNames(
                        (Array.isArray(legislation.exemptions) ? legislation.exemptions : [])
                            .map(normalizeExemptionOption),
                    ),
                    disabled: this.legislations.some(selected => (
                        this.regulationsMatch(legislation, selected)
                    )),
                }))
        },
        allRegulationsSelected() {
            return this.legislationOptions.every(option => option.disabled)
        },
        visibleRegulationEntries() {
            return this.legislations.reduce((entries, regulation, index) => {
                if (regulationVisibilityPolicyRegistry.isVisible(
                    this.visibilityPolicyNames(regulation),
                    {
                        regulation,
                        legislations: this.legislations,
                        scipInformation: this.scipInformation,
                    },
                )) {
                    entries.push({ regulation, index })
                }

                return entries
            }, [])
        },
        visibleRegulationIndicesKey() {
            return JSON.stringify(this.visibleRegulationEntries.map(entry => entry.index))
        },
        activeRegulation() {
            return this.activeRegulationIndex === null
                ? null
                : this.legislations[this.activeRegulationIndex] || null
        },
        activeContentVariant() {
            return this.regulationContentVariant(this.activeRegulation)
        },
        activeContentComponent() {
            return resolveGuidedRegulationContent(this.activeContentVariant)
                || resolveGuidedRegulationContent(GuidedRegulationContentVariant.SUBSTANCE_V1)
        },
        hasScipNumber() {
            return !!(this.scipInformation
                && typeof this.scipInformation.scipNumber === 'string'
                && this.scipInformation.scipNumber.trim())
        },
        hasScipRegulation() {
            return this.legislations.some(regulation => this.isScipRegulation(regulation))
        },
        scipRegulationNormalizationKey() {
            return JSON.stringify([
                this.ensureScipRegulationForNumber,
                this.hasScipNumber,
                this.hasScipRegulation,
            ])
        },
        activeContentProps() {
            if (this.activeContentVariant === GuidedRegulationContentVariant.SCIP_V1) {
                return {
                    scipInformation: this.scipInformation,
                    violations: this.violations,
                }
            }

            return {
                regulationIndex: this.activeRegulationIndex,
                regulationId: this.activeAvailableLegislation
                    ? this.activeAvailableLegislation.id
                    : null,
                showSubstanceDeclaration: this.showSubstanceDeclaration,
                substanceClaimOptions: this.substanceClaimOptions,
                selectedSubstanceClaim: this.selectedSubstanceClaim,
                hasAmbiguousSubstanceClaims: this.hasAmbiguousSubstanceClaims,
                showActiveSubstancesSection: this.showActiveSubstancesSection,
                selectedSubstanceClaimLabel: this.selectedSubstanceClaimLabel,
                canAddSubstance: this.canAddSubstance,
                activeSubstanceItems: this.activeSubstanceItems.map(item => ({
                    ...item,
                    defaultConcentrationBasis: this.manualSubstanceDefaultBasis(item),
                    violations: this.substanceViolations(item.index),
                })),
                hydratingIds: this.hydratingIds,
                showExemptions: this.showActiveExemptions,
                exemptionsSupported: this.activeRegulationCapabilities.exemptions,
                exemptionNames: this.activeExemptionNames,
                exemptionOptions: this.activeExemptionOptions,
            }
        },
        substanceClaimOptions() {
            return SUBSTANCE_CLAIM_OPTIONS.map(value => ({
                value,
                label: this.translate(
                    `Component(ResearchReviewProcessEnum):claim-type-${value}`,
                    this.humanize(value),
                ),
            }))
        },
        activeSubstanceClaims() {
            const claims = this.activeRegulation && Array.isArray(this.activeRegulation.claims)
                ? this.activeRegulation.claims
                : []

            return claims.filter(claim => claim && MANAGED_SUBSTANCE_CLAIMS.includes(claim.type))
        },
        selectedSubstanceClaim() {
            const selected = this.activeSubstanceClaims
                .filter(claim => SUBSTANCE_CLAIM_OPTIONS.includes(claim.type))

            return this.activeSubstanceClaims.length === 1 && selected.length === 1
                ? selected[0].type
                : null
        },
        hasAmbiguousSubstanceClaims() {
            return this.activeSubstanceClaims.length > 0 && !this.selectedSubstanceClaim
        },
        activeRegulationKey() {
            return this.activeRegulation
                ? this.regulationTabKey(this.activeRegulation, this.activeRegulationIndex)
                : null
        },
        activeAvailableLegislation() {
            return this.availableLegislationFor(this.activeRegulation)
        },
        activeRegulationCapabilities() {
            return this.regulationCapabilities(this.activeRegulation)
        },
        activeRegulationExemptions() {
            return this.exemptions.filter(exemption => (
                belongsToRegulation(exemption, this.activeRegulation)
            ))
        },
        activeExemptionNames() {
            return uniqueExemptionNames(this.activeRegulationExemptions
                .map(exemption => exemption && exemption.name))
        },
        activeExemptionOptions() {
            return this.activeAvailableLegislation
                ? this.activeAvailableLegislation.exemptions
                : []
        },
        showActiveExemptions() {
            return this.activeRegulationCapabilities.exemptions
                || this.activeRegulationExemptions.length > 0
        },
        hasSubstanceDeclarationConflict() {
            return this.hasAmbiguousSubstanceClaims
                || (this.selectedSubstanceClaim === 'does_not_contain_restricted_substances'
                    && this.activeSubstanceItems.length > 0)
                || this.hasThresholdDeclarationConflict(
                    this.activeRegulation,
                    this.selectedSubstanceClaim,
                )
        },
        showSubstanceDeclaration() {
            return !this.activeSubstanceItems.length
                || !this.selectedSubstanceClaim
                || this.hasSubstanceDeclarationConflict
                || !!this.editingSubstanceDeclarations[this.activeRegulationKey]
        },
        selectedSubstanceClaimLabel() {
            const option = this.substanceClaimOptions.find(item => (
                item.value === this.selectedSubstanceClaim
            ))

            return option ? option.label : ''
        },
        activeRemovalSummary() {
            if (!this.activeRegulation) {
                return null
            }

            const claims = Array.isArray(this.activeRegulation.claims)
                ? this.activeRegulation.claims.length
                : 0
            const exemptions = this.exemptions.filter(exemption => (
                belongsToRegulation(exemption, this.activeRegulation)
            )).length
            const substances = this.regulationSubstanceCount(this.activeRegulation)
            const hasEvidence = typeof this.activeRegulation.evidence === 'string'
                && this.activeRegulation.evidence.trim().length > 0

            return {
                claims,
                exemptions,
                substances,
                hasEvidence,
                hasData: !!(claims || exemptions || substances || hasEvidence),
            }
        },
        removeRegulationLabel() {
            const name = this.activeRegulation
                ? this.regulationTabLabel(this.activeRegulation, this.activeRegulationIndex)
                : ''

            return this.translate(
                'Component(ResearchReviewProcess):remove-regulation',
                'Remove {regulation}',
            ).replace('{regulation}', name)
        },
        canAddSubstance() {
            return ADDABLE_SUBSTANCE_CLAIMS.includes(this.selectedSubstanceClaim)
        },
        showActiveSubstancesSection() {
            if (this.hideSubstancesUntilDeclaration
                && !this.selectedSubstanceClaim
                && !this.activeSubstanceItems.length) {
                return false
            }
            if (!this.hideEmptyNegativeSubstances) {
                return true
            }

            const hasRecoveryData = this.unmatchedSubstanceItems.length
                || this.substanceViewModel.unassignedLinks.length
                || this.missingRegulationSubstanceIds.length
            const isEmptyNegativeDeclaration = this.selectedSubstanceClaim
                === 'does_not_contain_restricted_substances'
                && this.activeSubstanceItems.length === 0

            return !isEmptyNegativeDeclaration || !!hasRecoveryData
        },
        linkedSubstanceIds() {
            return Array.from(new Set(this.substanceLinks
                .map(link => link && link.substanceId ? Number(link.substanceId) : null)
                .filter(id => Number.isInteger(id) && id > 0)))
        },
        linkedSubstanceIdsKey() {
            return this.linkedSubstanceIds.join(',')
        },
        substanceViewModel() {
            return buildSubstanceReviewViewModel(
                this.substances,
                this.substanceLinks,
                this.summariesById,
                substanceReviewCheckerRegistry,
            )
        },
        activeSubstanceItems() {
            return this.regulationSubstanceItems(this.activeRegulation)
        },
        assignedExtractedSubstanceIds() {
            const ids = new Set()
            this.legislations.forEach(regulation => {
                getRelatedSubstances(this.relatedSubstancesProjection, regulation).forEach(item => {
                    if (item && hasIdentity(item.extractedSubstanceId)) {
                        ids.add(String(item.extractedSubstanceId))
                    }
                })
            })
            Object.keys(this.pendingRegulationIdsByExtractedId).forEach(id => {
                if (this.selectedLegislationIds.includes(
                    this.pendingRegulationIdsByExtractedId[id],
                )) {
                    ids.add(String(id))
                }
            })

            return ids
        },
        unmatchedSubstanceItems() {
            return this.substanceViewModel.items.filter(item => (
                !hasIdentity(item.substance.extractedSubstanceId)
                || !this.assignedExtractedSubstanceIds.has(String(item.substance.extractedSubstanceId))
            ))
        },
        hasUnassignedReviewIssues() {
            const hasIncompleteMatches = this.substanceViewModel.items.some(item => !(
                item.link
                && item.link.substanceId
                && item.link.status === 'accepted'
            ))

            return !!(
                hasIncompleteMatches
                || this.unmatchedSubstanceItems.length
                || this.substanceViewModel.unassignedLinks.length
                || this.missingRegulationSubstanceIds.length
                || this.relatedSubstancesError
                || this.resolvingRelatedSubstances
            )
        },
        blockingCompletionIssues() {
            const regulationIssues = this.visibleRegulationEntries.reduce((issues, entry) => (
                issues.concat(this.tabAttentionChecks(entry.regulation, entry.index)
                    .filter(check => (
                        check
                        && check.blocking
                        && check.status === 'needs_review'
                        && check.reason
                    )))
            ), [])

            if (!this.hasUnassignedReviewIssues) {
                return regulationIssues
            }

            return regulationIssues.concat({
                id: 'substance-matching',
                blocking: true,
                status: 'needs_review',
                reason: {
                    messageKey: 'Component(ResearchReviewProcess):reason-resolve-substance-matching',
                    fallbackMessage: 'Resolve substance matching issues before completing the review.',
                    params: {},
                },
            })
        },
        completionStateKey() {
            return JSON.stringify(this.blockingCompletionIssues)
        },
        substanceDeclarationInferenceKey() {
            if (this.substanceDeclarationInference.mode !== 'apply-when-empty') {
                return null
            }

            return JSON.stringify({
                resolving: this.resolvingRelatedSubstances,
                failed: this.relatedSubstancesError,
                regulations: this.legislations.map(regulation => ({
                    key: regulation && regulation.key,
                    claims: (Array.isArray(regulation && regulation.claims)
                        ? regulation.claims
                        : []).map(claim => claim && claim.type),
                    substances: this.regulationSubstanceItems(regulation).map(item => ({
                        id: item.substance && item.substance.extractedSubstanceId,
                        concentration: item.substance && item.substance.concentration,
                    })),
                })),
            })
        },
    },
    watch: {
        violations: {
            deep: true,
            handler() {
                this.focusFirstSubstanceViolation()
            },
        },
        visibleRegulationIndicesKey: {
            immediate: true,
            handler() {
                this.ensureActiveRegulationVisible()
            },
        },
        linkedSubstanceIdsKey: {
            immediate: true,
            handler() {
                this.hydrateLinkedSubstances()
            },
        },
        relatedSubstancesProjection: {
            deep: true,
            handler() {
                this.reconcilePendingRegulationAssignments()
            },
        },
        resolvingRelatedSubstances(value, previous) {
            if (previous && !value) {
                this.finishPendingRegulationAssignments()
            }
        },
        completionStateKey: {
            immediate: true,
            handler() {
                this.$emit('completion-validity-change', !this.blockingCompletionIssues.length)
                this.$emit('completion-issues-change', clone(this.blockingCompletionIssues))
            },
        },
        scipRegulationNormalizationKey: {
            immediate: true,
            handler() {
                this.ensureScipRegulation()
            },
        },
        substanceDeclarationInferenceKey: {
            immediate: true,
            handler(key) {
                if (key) {
                    this.applySubstanceDeclarationInference()
                }
            },
        },
    },
    beforeDestroy() {
        this.hydrationRevision += 1
    },
    methods: {
        substanceViolations(index) {
            return this.violations.filter(violation => {
                const target = substanceViolationTarget(violation)

                return target && target.index === index
            })
        },
        focusFirstSubstanceViolation() {
            const target = this.violations
                .map(substanceViolationTarget)
                .find(Boolean)
            if (!target) {
                return
            }

            const activeContainsSubstance = this.activeRegulation
                && this.regulationSubstanceItems(this.activeRegulation)
                    .some(item => item.index === target.index)
            const regulationIndex = activeContainsSubstance
                ? this.activeRegulationIndex
                : (this.visibleRegulationEntries.find(entry => (
                    this.regulationSubstanceItems(entry.regulation)
                        .some(item => item.index === target.index)
                )) || {}).index
            if (regulationIndex >= 0) {
                this.activeRegulationIndex = regulationIndex
            }

            this.$nextTick(() => {
                const activeContent = this.$refs.activeContent
                if (activeContent
                    && typeof activeContent.focusSubstanceViolation === 'function'
                    && activeContent.focusSubstanceViolation(target.index, target.field)) {
                    return
                }

                const unassigned = this.$refs.unassignedSubstances
                if (unassigned && typeof unassigned.focusSubstanceViolation === 'function') {
                    unassigned.focusSubstanceViolation(target.index, target.field)
                }
            })
        },
        translate(key, fallback) {
            const translation = this.$t(key)

            return translation && translation !== key ? translation : fallback
        },
        humanize(value) {
            return String(value || '')
                .replace(/_/g, ' ')
                .replace(/\b\w/g, letter => letter.toUpperCase())
        },
        ensureActiveRegulationVisible() {
            const indices = this.visibleRegulationEntries.map(entry => entry.index)
            if (!indices.length) {
                this.activeRegulationIndex = null
                return
            }
            if (indices.includes(this.activeRegulationIndex)) {
                return
            }

            const current = Number.isInteger(this.activeRegulationIndex)
                ? this.activeRegulationIndex
                : indices[0]
            this.activeRegulationIndex = indices.find(index => index >= current)
                || indices[indices.length - 1]
        },
        visibilityPolicyNames(regulation) {
            const key = String(regulation && regulation.key ? regulation.key : '')
                .trim()
                .toLowerCase()
            const names = this.visibilityPolicies[key]

            return Array.isArray(names) ? names : []
        },
        isScipRegulation(regulation) {
            return String(regulation && regulation.key ? regulation.key : '')
                .trim()
                .toLowerCase() === 'scip'
        },
        legislationLabel(key) {
            return this.translate(
                `Component(ResearchReviewProcessEnum):legislation-key-${key}`,
                this.humanize(key),
            )
        },
        regulationTabLabel(regulation, index) {
            if (regulation && regulation.name) {
                return regulation.name
            }
            if (regulation && regulation.key) {
                return this.legislationLabel(regulation.key)
            }

            return this.translate(
                'Component(ResearchReviewProcess):unnamed-legislation',
                `Unnamed regulation ${Number(index) + 1}`,
            )
        },
        regulationTabKey(regulation, index) {
            if (regulation && regulation.id !== null && typeof regulation.id !== 'undefined') {
                return `regulation-id-${regulation.id}`
            }
            if (regulation && regulation.key) {
                return `regulation-key-${regulation.key}-${index}`
            }

            return `regulation-index-${index}`
        },
        regulationSubstanceItems(regulation) {
            const relatedIds = new Set(getRelatedSubstances(
                this.relatedSubstancesProjection,
                regulation,
            ).map(item => String(item.extractedSubstanceId)))
            const availableRegulation = this.availableLegislationFor(regulation)
            const regulationId = availableRegulation ? availableRegulation.id : null

            Object.keys(this.pendingRegulationIdsByExtractedId).forEach(id => {
                if (regulationId
                    && this.pendingRegulationIdsByExtractedId[id] === regulationId) {
                    relatedIds.add(String(id))
                }
            })

            return this.substanceViewModel.items.filter(item => (
                hasIdentity(item.substance.extractedSubstanceId)
                && relatedIds.has(String(item.substance.extractedSubstanceId))
            ))
        },
        regulationSubstanceCount(regulation) {
            return this.regulationSubstanceItems(regulation).length
        },
        inferenceLegislation(regulation) {
            const available = this.availableLegislationFor(regulation)

            return {
                ...(available || {}),
                ...(regulation || {}),
                reviewPolicy: available && available.reviewPolicy
                    ? available.reviewPolicy
                    : regulation && regulation.reviewPolicy,
            }
        },
        applySubstanceDeclarationInference() {
            if (!this.initialSubstanceDeclarationInferencePending) {
                return
            }
            if (this.substanceDeclarationInference.mode !== 'apply-when-empty') {
                this.initialSubstanceDeclarationInferencePending = false
                return
            }
            if (this.resolvingRelatedSubstances) {
                return
            }

            this.initialSubstanceDeclarationInferencePending = false
            if (this.relatedSubstancesError) {
                return
            }

            let changed = false
            const legislations = this.legislations.map(regulation => {
                const claims = Array.isArray(regulation && regulation.claims)
                    ? regulation.claims
                    : []
                const managedClaims = claims.filter(claim => (
                    claim && MANAGED_SUBSTANCE_CLAIMS.includes(claim.type)
                ))
                const substances = this.regulationSubstanceItems(regulation)
                    .map(item => item.substance)
                const reconcilesAbsentDeclaration = managedClaims.length === 1
                    && managedClaims[0].type === 'does_not_contain_restricted_substances'
                    && substances.length > 0
                const reconcilesEmptyThresholdDeclaration = managedClaims.length === 1
                    && managedClaims[0].type === 'no_substances_above_threshold'
                    && substances.length === 0
                    && !this.hasUnassignedReviewIssues
                    && this.regulationContentVariant(regulation)
                        === GuidedRegulationContentVariant.SUBSTANCE_V1
                if (managedClaims.length
                    && !reconcilesAbsentDeclaration
                    && !reconcilesEmptyThresholdDeclaration) {
                    return regulation
                }

                const evaluation = substanceDeclarationPolicyEvaluator.evaluate({
                    legislation: this.inferenceLegislation(regulation),
                    substances,
                    claims,
                })
                const appliesGenericEmptyFallback = (
                    !managedClaims.length || reconcilesEmptyThresholdDeclaration
                )
                    && substances.length === 0
                    && !this.hasUnassignedReviewIssues
                    && this.regulationContentVariant(regulation)
                        === GuidedRegulationContentVariant.SUBSTANCE_V1
                    && !['backend', 'backend-invalid'].includes(evaluation.policySource)
                const inferredClaimType = evaluation.status === 'resolved'
                    && evaluation.claimType
                    ? evaluation.claimType
                    : appliesGenericEmptyFallback
                        ? 'does_not_contain_restricted_substances'
                        : null
                if (!inferredClaimType) {
                    return regulation
                }
                if (reconcilesAbsentDeclaration
                    && !ADDABLE_SUBSTANCE_CLAIMS.includes(inferredClaimType)) {
                    return regulation
                }

                changed = true
                return {
                    ...regulation,
                    claims: claims
                        .filter(claim => (
                            !claim || !MANAGED_SUBSTANCE_CLAIMS.includes(claim.type)
                        ))
                        .concat({
                            type: inferredClaimType,
                            evidence: reconcilesEmptyThresholdDeclaration
                                ? managedClaims[0].evidence || null
                                : null,
                        }),
                }
            })

            if (changed) {
                this.emitState({ legislations })
            }
        },
        tabSubstanceCount(regulation) {
            return this.regulationContentVariant(regulation)
                === GuidedRegulationContentVariant.SUBSTANCE_V1
                ? this.regulationSubstanceCount(regulation)
                : 0
        },
        regulationContentVariant(regulation) {
            const key = regulation && regulation.key
                ? String(regulation.key).trim().toLowerCase()
                : ''
            const byLegislation = this.content && this.content.byLegislation
                && typeof this.content.byLegislation === 'object'
                ? this.content.byLegislation
                : {}

            return byLegislation[key]
                || this.content.default
                || GuidedRegulationContentVariant.SUBSTANCE_V1
        },
        ensureScipRegulation() {
            if (!this.ensureScipRegulationForNumber
                || !this.hasScipNumber
                || this.hasScipRegulation) {
                return
            }

            const option = this.legislationOptions.find(item => (
                String(item.key || '').trim().toLowerCase() === 'scip'
            ))
            this.activeRegulationIndex = this.legislations.length
            this.emitState({
                legislations: clone(this.legislations).concat({
                    name: option ? option.label : 'SCIP',
                    status: null,
                    key: 'scip',
                    evidence: null,
                    claims: [],
                }),
            })
        },
        tabAttentionChecks(regulation, index) {
            if (this.regulationContentVariant(regulation)
                === GuidedRegulationContentVariant.SCIP_V1) {
                const hasScipNumber = !!(this.scipInformation
                    && typeof this.scipInformation.scipNumber === 'string'
                    && this.scipInformation.scipNumber.trim())

                return [createAttentionCheck(
                    `regulation-${index}-scip-number`,
                    hasScipNumber,
                    'Component(ResearchReviewProcess):reason-add-scip-number',
                    'Add the SCIP number.',
                )]
            }

            const claims = Array.isArray(regulation && regulation.claims)
                ? regulation.claims
                : []
            const managedClaims = claims.filter(claim => (
                claim && MANAGED_SUBSTANCE_CLAIMS.includes(claim.type)
            ))
            const selectedClaims = managedClaims.filter(claim => (
                SUBSTANCE_CLAIM_OPTIONS.includes(claim.type)
            ))
            const selectedClaim = managedClaims.length === 1 && selectedClaims.length === 1
                ? selectedClaims[0].type
                : null
            const substanceItems = this.regulationSubstanceItems(regulation)
            const relatedExemptions = this.exemptions.filter(exemption => (
                belongsToRegulation(exemption, regulation)
            ))
            const declarationPolicy = legislationReviewPolicyProvider
                .resolveSubstanceDeclaration(this.inferenceLegislation(regulation))
            const regulationName = this.regulationTabLabel(regulation, index)
            const conflictBlocking = declarationPolicy.policy
                && declarationPolicy.policy.reconciliation
                && declarationPolicy.policy.reconciliation.onConflict === 'block'
            const thresholdConflict = this.hasThresholdDeclarationConflict(
                regulation,
                selectedClaim,
            )
            const checks = [
                createAttentionCheck(
                    `regulation-${index}-substance-declaration`,
                    !!selectedClaim,
                    'Component(ResearchReviewProcess):reason-select-substance-declaration-for-regulation',
                    `Select a substance declaration for ${regulationName}.`,
                    {
                        blocking: declarationPolicy.policy
                            && declarationPolicy.policy.requiredForCompletion === true,
                        params: { regulation: regulationName },
                    },
                ),
                createAttentionCheck(
                    `regulation-${index}-substance-conflict`,
                    selectedClaim !== 'does_not_contain_restricted_substances'
                        || substanceItems.length === 0,
                    'Component(ResearchReviewProcess):reason-resolve-substance-declaration-conflict',
                    'The declaration says no restricted substances are present, but linked substances are listed. Change the declaration or remove the substances.',
                    { blocking: conflictBlocking },
                ),
                createAttentionCheck(
                    `regulation-${index}-missing-substances`,
                    !ADDABLE_SUBSTANCE_CLAIMS.includes(selectedClaim)
                        || substanceItems.length > 0,
                    'Component(ResearchReviewProcess):reason-add-declared-substance',
                    'Add at least one substance or change the declaration.',
                    { blocking: conflictBlocking },
                ),
                createAttentionCheck(
                    `regulation-${index}-substance-threshold-conflict`,
                    !thresholdConflict,
                    'Component(ResearchReviewProcess):reason-resolve-substance-declaration-threshold-conflict',
                    `The substance declaration for ${regulationName} conflicts with the associated substance concentrations. Change the declaration or substance values.`,
                    {
                        blocking: conflictBlocking,
                        params: { regulation: regulationName },
                    },
                ),
                createAttentionCheck(
                    `regulation-${index}-unsupported-exemptions`,
                    this.regulationCapabilities(regulation).exemptions
                        || relatedExemptions.length === 0,
                    'Component(ResearchReviewProcess):reason-remove-unsupported-regulation-exemptions',
                    'Remove exemptions that are not supported for this regulation.',
                ),
            ]

            substanceItems.forEach(item => {
                ;(item.evaluation.checks || []).forEach(check => {
                    checks.push({
                        ...check,
                        id: `regulation-${index}-${item.rowKey}-${check.id}`,
                    })
                })
            })

            return checks
        },
        hasThresholdDeclarationConflict(regulation, selectedClaim) {
            if (!regulation
                || !ADDABLE_SUBSTANCE_CLAIMS.includes(selectedClaim)
                || this.resolvingRelatedSubstances
                || this.relatedSubstancesError) {
                return false
            }

            const evaluation = substanceDeclarationPolicyEvaluator.evaluate({
                legislation: this.inferenceLegislation(regulation),
                substances: this.regulationSubstanceItems(regulation)
                    .map(item => item.substance),
                claims: Array.isArray(regulation.claims) ? regulation.claims : [],
            })

            return evaluation.status === 'resolved'
                && ADDABLE_SUBSTANCE_CLAIMS.includes(evaluation.claimType)
                && evaluation.claimType !== selectedClaim
        },
        selectRegulation(index) {
            if (this.legislations[index]) {
                this.activeRegulationIndex = index
            }
        },
        addRegulation(id) {
            const option = this.legislationOptions.find(item => item.id === Number(id))
            if (!option || option.disabled) {
                return
            }

            this.activeRegulationIndex = this.legislations.length
            this.emitState({
                legislations: clone(this.legislations).concat({
                    name: option.label,
                    status: null,
                    key: option.key,
                    evidence: null,
                    claims: [],
                }),
            })
        },
        async requestRemoveActiveRegulation() {
            if (!this.allowRemoveRegulation || !this.activeRegulation) {
                return
            }
            if (this.activeRemovalSummary && this.activeRemovalSummary.hasData) {
                const result = await this.$swal({
                    title: this.removeRegulationLabel,
                    text: this.translate(
                        'Component(ResearchReviewProcess):remove-regulation-description',
                        'The regulation and its related review data will be removed.',
                    ),
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: this.translate('Component(Button):remove', 'Remove'),
                    cancelButtonText: this.translate('Component(Button):cancel', 'Cancel'),
                    customClass: {
                        confirmButton: 'btn btn-danger',
                        cancelButton: 'btn btn-outline-secondary ml-1',
                    },
                    buttonsStyling: false,
                })

                if (!result.isConfirmed) {
                    return
                }
            }

            this.confirmRemoveRegulation()
        },
        confirmRemoveRegulation() {
            if (!this.activeRegulation || this.activeRegulationIndex === null) {
                return
            }

            const removedRegulation = this.activeRegulation
            const removedIndex = this.activeRegulationIndex
            const legislations = this.legislations.filter((regulation, index) => (
                index !== removedIndex
            ))
            const exemptions = this.exemptions.filter(exemption => (
                !belongsToRegulation(exemption, removedRegulation)
            ))

            this.$delete(
                this.editingSubstanceDeclarations,
                this.regulationTabKey(removedRegulation, removedIndex),
            )
            this.activeRegulationIndex = legislations.length
                ? Math.min(removedIndex, legislations.length - 1)
                : null
            const exclusiveSubstanceIds = this.regulationContentVariant(removedRegulation)
                === GuidedRegulationContentVariant.SUBSTANCE_V1
                ? this.exclusiveRegulationSubstanceIds(removedRegulation, legislations)
                : new Set()
            const substances = this.substances.filter(substance => (
                !substance
                || !hasIdentity(substance.extractedSubstanceId)
                || !exclusiveSubstanceIds.has(String(substance.extractedSubstanceId))
            ))
            const links = this.substanceLinks.filter(link => (
                !link
                || !hasIdentity(link.extractedSubstanceId)
                || !exclusiveSubstanceIds.has(String(link.extractedSubstanceId))
            ))

            exclusiveSubstanceIds.forEach(id => {
                this.$delete(this.pendingRegulationIdsByExtractedId, String(id))
                this.$delete(this.manualSubstanceSettingsByExtractedId, String(id))
            })

            const update = {
                legislations,
                exemptions,
                substances,
                links,
            }
            if (this.isScipRegulation(removedRegulation) && this.hasScipNumber) {
                const scipInformation = clone(this.scipInformation)
                delete scipInformation.scipNumber
                update.scipInformation = Object.keys(scipInformation).length
                    ? scipInformation
                    : null
            }

            this.emitState(update)
        },
        exclusiveRegulationSubstanceIds(regulation, remainingLegislations) {
            const activeIds = new Set(this.regulationSubstanceItems(regulation)
                .map(item => item && item.substance && item.substance.extractedSubstanceId)
                .filter(hasIdentity)
                .map(String))
            const sharedIds = new Set()

            remainingLegislations.forEach(item => {
                this.regulationSubstanceItems(item).forEach(substanceItem => {
                    const id = substanceItem
                        && substanceItem.substance
                        && substanceItem.substance.extractedSubstanceId
                    if (hasIdentity(id)) {
                        sharedIds.add(String(id))
                    }
                })
            })

            return new Set(Array.from(activeIds).filter(id => !sharedIds.has(id)))
        },
        emitState({
            legislations = this.legislations,
            exemptions = this.exemptions,
            substances = this.substances,
            links = this.substanceLinks,
            scipInformation = undefined,
        }) {
            const update = {
                legislations: clone(legislations),
                exemptions: clone(exemptions),
                substances: clone(substances),
                links: clone(links),
            }
            if (typeof scipInformation !== 'undefined') {
                update.scipInformation = scipInformation === null
                    ? null
                    : clone(scipInformation)
            }
            this.$emit('update', update)
        },
        selectSubstanceClaim(type) {
            if (!this.activeRegulation || !SUBSTANCE_CLAIM_OPTIONS.includes(type)) {
                return
            }
            if (this.selectedSubstanceClaim === type) {
                this.$delete(this.editingSubstanceDeclarations, this.activeRegulationKey)
                return
            }

            const claims = (Array.isArray(this.activeRegulation.claims)
                ? this.activeRegulation.claims
                : [])
                .filter(claim => claim && !MANAGED_SUBSTANCE_CLAIMS.includes(claim.type))
                .concat({ type, evidence: null })
            const legislations = this.legislations.map((regulation, index) => (
                index === this.activeRegulationIndex
                    ? { ...regulation, claims }
                    : regulation
            ))

            this.$delete(this.editingSubstanceDeclarations, this.activeRegulationKey)
            this.emitState({ legislations })
        },
        editSubstanceDeclaration() {
            if (this.activeRegulationKey) {
                this.$set(this.editingSubstanceDeclarations, this.activeRegulationKey, true)
            }
        },
        addSubstance() {
            if (!this.canAddSubstance) {
                return
            }

            const extractedSubstanceId = uuidv4()
            const { policy } = legislationReviewPolicyProvider.resolveManualSubstance(
                this.inferenceLegislation(this.activeRegulation),
            )
            this.$set(this.manualSubstanceSettingsByExtractedId, extractedSubstanceId, {
                policy: clone(policy),
                consumed: false,
            })
            if (this.activeAvailableLegislation) {
                this.$set(
                    this.pendingRegulationIdsByExtractedId,
                    extractedSubstanceId,
                    this.activeAvailableLegislation.id,
                )
            }
            this.emitState({
                substances: this.substances.concat({
                    extractedSubstanceId,
                    name: '',
                    ecNumber: null,
                    casNumber: null,
                    concentration: null,
                }),
            })
        },
        updateSubstance(index, substance) {
            this.emitState({
                substances: this.substances.map((item, itemIndex) => (
                    itemIndex === index ? substance : item
                )),
            })
        },
        updateActiveExemptions(values) {
            if (!this.activeRegulation) {
                return
            }

            const existing = this.activeRegulationExemptions
            const existingByName = existing.reduce((result, exemption) => {
                const name = normalizeExemptionName(exemption && exemption.name)
                if (name && !result.has(name.toLowerCase())) {
                    result.set(name.toLowerCase(), exemption)
                }

                return result
            }, new Map())
            const requestedNames = uniqueExemptionNames(values)
            const names = this.activeRegulationCapabilities.exemptions
                ? requestedNames
                : requestedNames.filter(name => existingByName.has(name.toLowerCase()))
            const available = this.activeAvailableLegislation
            const replacements = names.map(name => (
                existingByName.get(name.toLowerCase()) || {
                    name,
                    evidence: null,
                    legislation: this.activeRegulation.name
                        || this.activeRegulation.title
                        || (available ? available.label : null),
                    legislationId: available
                        ? available.id
                        : this.activeRegulation.id || null,
                    legislationKey: this.activeRegulation.key
                        || (available ? available.key : null),
                }
            ))
            let inserted = false
            const exemptions = this.exemptions.reduce((result, exemption) => {
                if (belongsToRegulation(exemption, this.activeRegulation)) {
                    if (!inserted) {
                        result.push(...replacements)
                        inserted = true
                    }
                    return result
                }

                result.push(exemption)
                return result
            }, [])

            if (!inserted) {
                exemptions.push(...replacements)
            }
            this.emitState({ exemptions })
        },
        removeSubstance(index) {
            const substance = this.substances[index]
            const extractedSubstanceId = substance && substance.extractedSubstanceId
            if (hasIdentity(extractedSubstanceId)) {
                this.$delete(
                    this.pendingRegulationIdsByExtractedId,
                    String(extractedSubstanceId),
                )
                this.$delete(
                    this.manualSubstanceSettingsByExtractedId,
                    String(extractedSubstanceId),
                )
            }
            const links = hasIdentity(extractedSubstanceId)
                ? this.substanceLinks.filter(link => !link
                    || !hasIdentity(link.extractedSubstanceId)
                    || String(link.extractedSubstanceId) !== String(extractedSubstanceId))
                : this.substanceLinks

            this.emitState({
                substances: this.substances.filter((item, itemIndex) => itemIndex !== index),
                links,
            })
        },
        selectSubstanceLink(item, selected) {
            this.commitSubstanceLink(
                item,
                selected,
                this.activeAvailableLegislation ? this.activeAvailableLegislation.id : null,
            )
        },
        selectUnassignedSubstanceLink(item, selected) {
            this.commitSubstanceLink(item, selected)
        },
        commitSubstanceLink(item, selected, pendingRegulationId = null) {
            let extractedSubstanceId = item.substance.extractedSubstanceId
            let substances = this.substances

            if (!hasIdentity(extractedSubstanceId)) {
                extractedSubstanceId = uuidv4()
                substances = this.substances.map((substance, index) => (
                    index === item.index ? { ...substance, extractedSubstanceId } : substance
                ))
            }

            if (!selected || !selected.id) {
                const links = item.linkIndex === null
                    ? this.substanceLinks
                    : this.substanceLinks.filter((link, index) => index !== item.linkIndex)
                this.emitState({ substances, links })
                return
            }

            const selectedSubstanceId = Number(selected.id)
            const currentSubstanceId = Number(item.link && item.link.substanceId)
            if (Number.isInteger(pendingRegulationId)
                && pendingRegulationId > 0
                && selectedSubstanceId !== currentSubstanceId) {
                this.$set(
                    this.pendingRegulationIdsByExtractedId,
                    String(extractedSubstanceId),
                    pendingRegulationId,
                )
            }

            const summary = normalizeSummary(selected)
            substances = this.prefillManualSubstanceFromMatch(
                substances,
                extractedSubstanceId,
                summary,
            )
            this.$set(this.summariesById, String(summary.id), summary)
            const nextLink = {
                ...(item.link || {}),
                extractedSubstanceId,
                substanceId: Number(summary.id),
                type: 'manual',
                status: 'accepted',
                confidence: null,
                reason: null,
            }
            const links = item.linkIndex === null
                ? this.substanceLinks.concat(nextLink)
                : this.substanceLinks.map((link, index) => index === item.linkIndex ? nextLink : link)

            this.emitState({ substances, links })
        },
        manualSubstanceSetting(extractedSubstanceId) {
            return hasIdentity(extractedSubstanceId)
                ? this.manualSubstanceSettingsByExtractedId[String(extractedSubstanceId)] || null
                : null
        },
        manualSubstanceDefaultBasis(item) {
            const setting = item && item.substance
                ? this.manualSubstanceSetting(item.substance.extractedSubstanceId)
                : null

            return setting && setting.policy
                ? setting.policy.defaultConcentrationBasis
                : null
        },
        prefillManualSubstanceFromMatch(substances, extractedSubstanceId, summary) {
            const setting = this.manualSubstanceSetting(extractedSubstanceId)
            const prefill = setting && setting.policy && setting.policy.matchPrefill
            if (!prefill
                || prefill.mode === 'disabled'
                || (prefill.mode === 'prefill-once' && setting.consumed)) {
                return substances
            }

            const selectedValues = {
                name: summary.name || '',
                casNumber: summary.cas || null,
                ecNumber: summary.ec || null,
            }
            const fields = Array.isArray(prefill.fields) ? prefill.fields : []
            const nextSubstances = substances.map(substance => {
                if (!substance
                    || String(substance.extractedSubstanceId) !== String(extractedSubstanceId)) {
                    return substance
                }

                return fields.reduce((result, field) => {
                    const currentValue = result[field]
                    const canPrefill = prefill.mode === 'prefill-always'
                        || currentValue === null
                        || typeof currentValue === 'undefined'
                        || currentValue === ''

                    return canPrefill
                        ? { ...result, [field]: selectedValues[field] }
                        : result
                }, substance)
            })

            if (prefill.mode === 'prefill-once') {
                this.$set(setting, 'consumed', true)
            }

            return nextSubstances
        },
        regulationsMatch(candidate, regulation) {
            if (!candidate || !regulation) {
                return false
            }
            if (candidate.id !== null && typeof candidate.id !== 'undefined'
                && regulation.id !== null && typeof regulation.id !== 'undefined'
                && String(candidate.id) === String(regulation.id)) {
                return true
            }

            return !!candidate.key && !!regulation.key && candidate.key === regulation.key
        },
        availableLegislationFor(regulation) {
            if (!regulation) {
                return null
            }

            return this.legislationOptions.find(candidate => (
                this.regulationsMatch(candidate, regulation)
            )) || null
        },
        regulationCapabilities(regulation) {
            if (!regulation) {
                return resolveLegislationCapabilities()
            }

            const available = this.availableLegislationFor(regulation)
            const suppliedCapabilities = available
                && available.capabilities
                && typeof available.capabilities === 'object'
                ? available.capabilities
                : regulation.capabilities

            return resolveLegislationCapabilities({
                key: available ? available.key : regulation.key,
                capabilities: suppliedCapabilities,
            })
        },
        reconcilePendingRegulationAssignments() {
            Object.keys(this.pendingRegulationIdsByExtractedId).forEach(id => {
                if (this.canonicalAssignedExtractedSubstanceIds().has(String(id))) {
                    this.$delete(this.pendingRegulationIdsByExtractedId, id)
                }
            })
        },
        finishPendingRegulationAssignments() {
            const acceptedIds = new Set(this.substanceViewModel.items
                .filter(item => item.link
                    && item.link.substanceId
                    && item.link.status === 'accepted')
                .map(item => item.substance && item.substance.extractedSubstanceId)
                .filter(hasIdentity)
                .map(String))

            Object.keys(this.pendingRegulationIdsByExtractedId).forEach(id => {
                if (acceptedIds.has(String(id))) {
                    this.$delete(this.pendingRegulationIdsByExtractedId, id)
                }
            })
        },
        canonicalAssignedExtractedSubstanceIds() {
            const ids = new Set()
            this.legislations.forEach(regulation => {
                getRelatedSubstances(this.relatedSubstancesProjection, regulation).forEach(item => {
                    if (item && hasIdentity(item.extractedSubstanceId)) {
                        ids.add(String(item.extractedSubstanceId))
                    }
                })
            })

            return ids
        },
        hydrateLinkedSubstances() {
            const ids = this.linkedSubstanceIds.filter(id => !this.summariesById[String(id)])
            const revision = ++this.hydrationRevision
            const initializationTask = `substance-summaries-${revision}`

            if (!ids.length || typeof this.workspaceService.getSubstanceSummaries !== 'function') {
                this.hydratingIds = []
                return Promise.resolve()
            }

            this.$emit('initialization-start', initializationTask)
            this.hydratingIds = ids
            return this.workspaceService.getSubstanceSummaries(ids)
                .then(result => {
                    if (revision !== this.hydrationRevision) {
                        return
                    }
                    ;(result.items || []).forEach(summary => {
                        if (summary && summary.id) {
                            this.$set(this.summariesById, String(summary.id), normalizeSummary(summary))
                        }
                    })
                })
                .catch(() => undefined)
                .finally(() => {
                    if (revision === this.hydrationRevision) {
                        this.hydratingIds = []
                    }
                    this.$emit('initialization-end', initializationTask)
                })
        },
    },
}
</script>

<style lang="scss" scoped>
.guided-regulations-review-section__heading {
    display: flex;
    width: 100%;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
}

.guided-regulations-review-section__resolver {
    flex: 0 0 auto;
}

.guided-regulations-review-section__header-actions {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: .75rem;
    margin-left: auto;
}

.guided-regulations-review-section__tabs {
    display: flex;
    min-width: 0;
    align-items: stretch;
    border-bottom: 1px solid #d8d6de;
}

.guided-regulations-review-section__tabs-scroll {
    display: flex;
    flex: 1 1 auto;
    min-width: 0;
    align-items: stretch;
    overflow-x: auto;
    overflow-y: hidden;
}

.guided-regulations-review-section__tab-item {
    display: flex;
    flex: 0 0 auto;
    min-height: 3rem;
    align-items: center;
}

.guided-regulations-review-section__tab-attention {
    display: inline-flex;
    align-items: center;
}

.guided-regulations-review-section__tab-meta {
    display: inline-flex;
    height: 1.25rem;
    align-items: center;
    gap: .35rem;
    margin-left: .35rem;
}

.guided-regulations-review-section__tab-count {
    display: inline-flex;
    min-width: 1.25rem;
    height: 1.25rem;
    align-items: center;
    justify-content: center;
    padding: 0 .25rem;
    line-height: 1;
    vertical-align: middle;
}

::v-deep .guided-regulations-review-section__tab-attention .review-needs-attention-badge--compact {
    width: 1.25rem;
    height: 1.25rem;
    padding: 0;
    line-height: 1;
}

.guided-regulations-review-section__add {
    flex: 0 0 auto;
}

.guided-regulations-review-section__tab {
    position: relative;
    display: inline-flex;
    min-height: 3rem;
    align-items: center;
    padding: .75rem 1rem;
    border: 0;
    border-radius: .357rem .357rem 0 0;
    color: #6e6b7b;
    font-weight: 600;
    transition: color .15s ease, background-color .15s ease;
    white-space: nowrap;
}

.guided-regulations-review-section__tab::after {
    position: absolute;
    right: 0;
    bottom: -1px;
    left: 0;
    height: 2px;
    background: transparent;
    content: '';
}

.guided-regulations-review-section__tab:hover,
.guided-regulations-review-section__tab:focus {
    color: var(--primary);
    text-decoration: none;
}

.guided-regulations-review-section__tab--active,
.guided-regulations-review-section__tab--active:hover,
.guided-regulations-review-section__tab--active:focus {
    background: rgba(245, 128, 73, .06);
    color: var(--primary);
    text-decoration: none;
}

.guided-regulations-review-section__tab--active::after {
    background: var(--primary);
}

.guided-regulations-review-section__active-content {
    padding-top: 1rem;
}

@media (max-width: 767.98px) {
    .guided-regulations-review-section__heading {
        align-items: stretch;
        flex-direction: column;
    }

    .guided-regulations-review-section__header-actions {
        align-self: flex-end;
        margin-left: 0;
    }
}
</style>
